import { useEffect, useState } from 'react'
import kitApi, { ParsedRubricSection } from '@/api/kit/KitApi'
import Modal from '@/components/Modal'

type RubricViewModalProps = {
    screeningJobId: string
    jobTitle: string
    onClose: () => void
}

const SECTION_TYPE_COLORS: Record<string, string> = {
    KNOCKOUT: 'bg-red-100 text-red-700',
    BASICS: 'bg-blue-100 text-blue-700',
    EXPERIENCE: 'bg-purple-100 text-purple-700',
    BEHAVIOURS: 'bg-amber-100 text-amber-700',
}

function renderCriterionScores(
    sectionType: string,
    criterion: {
        passDescription: string | null
        flagDescription: string | null
        failDescription: string | null
        score1Description: string | null
        score2Description: string | null
        score3Description: string | null
    },
): React.ReactNode {
    if (sectionType === 'KNOCKOUT') {
        return null
    }

    if (sectionType === 'BASICS') {
        return (
            <ul className="ml-4 mt-1 space-y-0.5 text-xs text-gray-600">
                {criterion.passDescription && (
                    <li>
                        <span className="font-medium text-green-700">
                            Pass:
                        </span>{' '}
                        {criterion.passDescription}
                    </li>
                )}
                {criterion.flagDescription && (
                    <li>
                        <span className="font-medium text-yellow-700">
                            Flag:
                        </span>{' '}
                        {criterion.flagDescription}
                    </li>
                )}
                {criterion.failDescription && (
                    <li>
                        <span className="font-medium text-red-700">Fail:</span>{' '}
                        {criterion.failDescription}
                    </li>
                )}
            </ul>
        )
    }

    return (
        <ul className="ml-4 mt-1 space-y-0.5 text-xs text-gray-600">
            {criterion.score1Description && (
                <li>
                    <span className="font-medium">1:</span>{' '}
                    {criterion.score1Description}
                </li>
            )}
            {criterion.score2Description && (
                <li>
                    <span className="font-medium">2:</span>{' '}
                    {criterion.score2Description}
                </li>
            )}
            {criterion.score3Description && (
                <li>
                    <span className="font-medium">3:</span>{' '}
                    {criterion.score3Description}
                </li>
            )}
        </ul>
    )
}

export default function RubricViewModal({
    screeningJobId,
    jobTitle,
    onClose,
}: RubricViewModalProps): React.JSX.Element {
    const [sections, setSections] = useState<ParsedRubricSection[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        let cancelled = false

        const fetchRubric = async () => {
            try {
                const res = await kitApi.getRubric(screeningJobId)
                if (!cancelled) {
                    setSections(res.sections)
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load rubric', err)
                    setError(
                        err instanceof Error
                            ? err.message
                            : 'Failed to load rubric.',
                    )
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }

        void fetchRubric()

        return () => {
            cancelled = true
        }
    }, [screeningJobId])

    return (
        <Modal
            title={`Rubric - ${jobTitle}`}
            onClose={onClose}
            size="large"
            allowUserToClose={true}
        >
            <div className="flex flex-col gap-4">
                {error && (
                    <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {isLoading && (
                    <p className="text-sm text-gray-500">Loading rubric...</p>
                )}

                {!isLoading && sections && sections.length === 0 && (
                    <p className="text-sm text-gray-500">
                        No rubric data found.
                    </p>
                )}

                {!isLoading && sections && sections.length > 0 && (
                    <div className="max-h-[600px] overflow-y-auto rounded border bg-white p-3">
                        {sections.map((section, sIdx) => (
                            <div key={sIdx} className="mb-4 last:mb-0">
                                <div className="mb-1 flex items-center gap-2">
                                    <span
                                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${SECTION_TYPE_COLORS[section.sectionType] ?? 'bg-gray-100 text-gray-700'}`}
                                    >
                                        {section.sectionType}
                                    </span>
                                    <span className="text-sm font-medium text-gray-800">
                                        {section.label}
                                    </span>
                                </div>
                                <ol className="ml-4 list-decimal space-y-1">
                                    {section.criteria.map((criterion, cIdx) => (
                                        <li
                                            key={cIdx}
                                            className="text-sm text-gray-700"
                                        >
                                            {criterion.label}
                                            {renderCriterionScores(
                                                section.sectionType,
                                                criterion,
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    )
}
